/* ============================================================================
   EL ESTADO DE SINCRONIZACIÓN, EN REACT

   El motor vive fuera de React; este gancho lo arranca, se suscribe y traduce
   sus estados a algo que la pantalla puede mostrar. No guarda datos de vuelo:
   eso sigue en IndexedDB y en la bitácora.

   ═══ SE ARRANCA CON EL PERFIL, NO DESPUÉS ═══

   El perfil decide qué entidades se leen y se escriben. Un piloto no empuja la
   flota y el servidor no tiene por qué contestarle 403 para descubrirlo.
   ========================================================================== */

import { useCallback, useEffect, useState } from "react";

import { estadoVacio } from "./motor.js";
import {
    conflictoPendiente,
    detenerSincronizacion,
    estadoDeSincronizacion,
    iniciarSincronizacion,
    rechazosPendientes,
    resolverConflictoActual,
    sincronizarAhora,
    suscribirASincronizacion,
} from "./servicio.js";

export function useSincronizacion({ activa = true, perfil = null } = {}) {
    const [estado, setEstado] = useState(estadoVacio);
    const [conflicto, setConflicto] = useState(null);
    const [rechazos, setRechazos] = useState([]);
    const [resolviendo, setResolviendo] = useState(false);

    const refrescar = useCallback(async () => {
        const [siguiente, pendiente, sinEnviar] = await Promise.all([
            estadoDeSincronizacion(),
            conflictoPendiente(),
            rechazosPendientes(),
        ]);
        setEstado(siguiente);
        setConflicto(pendiente);
        setRechazos(sinEnviar);
    }, []);

    useEffect(() => {
        if (!activa) return undefined;
        let vivo = true;

        iniciarSincronizacion({ perfil });
        refrescar();

        const desuscribir = suscribirASincronizacion((siguiente) => {
            if (!vivo) return;
            setEstado(siguiente);
            /* El conflicto y los rechazos no viajan en cada publicación: se
               leen aparte. Es una lectura local, no una llamada de red. */
            conflictoPendiente().then((c) => vivo && setConflicto(c));
            rechazosPendientes().then((r) => vivo && setRechazos(r));
        });

        const reloj = setInterval(() => { if (vivo) refrescar(); }, 5000);

        return () => {
            vivo = false;
            desuscribir();
            clearInterval(reloj);
            detenerSincronizacion();
        };
    }, [activa, perfil, refrescar]);

    const sincronizar = useCallback(async () => {
        setEstado((previo) => ({ ...previo, fase: "sincronizando" }));
        await sincronizarAhora();
        await refrescar();
    }, [refrescar]);

    const resolver = useCallback(async (eleccion) => {
        if (!conflicto) return;
        setResolviendo(true);
        try {
            await resolverConflictoActual(conflicto.id, eleccion);
            await refrescar();
        } finally {
            setResolviendo(false);
        }
    }, [conflicto, refrescar]);

    return { estado, conflicto, rechazos, resolviendo, sincronizar, resolver, refrescar };
}
